import { useEffect, useState } from "react";
import { getDataReality, getRegistry } from "../lib/api";
import { DataTable } from "../components/DataTable";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { num, pct } from "../lib/format";

export default function DataRealityPage() {
  const [dr, setDr] = useState(null);
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState(null);
  const [coverageOf, setCoverageOf] = useState("grants");

  useEffect(() => {
    getDataReality().then(setDr).catch(setErr);
    getRegistry().then(setReg).catch(setErr);
  }, []);

  if (err) return <ErrorBox error={err} />;
  if (!dr) return <Loading label="Loading the Data Reality Report" />;

  const coverage = (dr.field_coverage?.[coverageOf] || [])
    .slice()
    .sort((a, b) => a.coverage_pct - b.coverage_pct);

  return (
    <div className="max-w-[1500px]">
      <PageHeader overline="Data truth" title="Data Reality Report" testId="reality-header">
        Profiled against the live mGrant instance as {dr.mgrant_user}. {dr.access_mode}.
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="reality-counts">
        <Tile label="Canonical grants" value={num(dr.canonical_counts.grants)} />
        <Tile label="Canonical partners" value={num(dr.canonical_counts.partners)} />
        <Tile label="LGD districts" value={num(dr.canonical_counts.lgd_districts)} />
        <Tile label="Name variants collapsed" value={num(dr.canonical_counts.partner_name_variants_collapsed)} />
      </div>

      {dr.committed_basis && (
        <Section title="Where the committed figure comes from"
                 sub="Every rupee the app calls committed traces to one mGrant field. That field is named here, with the check that it survives contact with the money.">
          <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-2"
               data-testid="committed-basis">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tile label="Field used" value={dr.committed_basis.field_used.split(".").pop()} />
              <Tile label={`${dr.committed_basis.mgrant_committed_field} populated`}
                    value={`${num(dr.committed_basis.grants_with_committed_field_populated)} of ${num(dr.committed_basis.grants_total)}`} />
              <Tile label="Received ÷ committed, ended grants"
                    value={pct(dr.committed_basis.received_over_committed_pct)} />
              <Tile label="Matching to the rupee"
                    value={`${num(dr.committed_basis.ended_grants_matching_to_the_rupee)} of ${num(dr.committed_basis.ended_grants_tested)}`} />
            </div>
            <p className="text-xs text-stone-700 leading-relaxed">{dr.committed_basis.verdict}</p>
            <p className="text-xs text-stone-600 leading-relaxed">{dr.committed_basis.caveat}</p>
          </div>
        </Section>
      )}

      <Section title="What the data cannot support"
               sub="Each blocker carries the record counts that prove it and the condition that would unlock it.">
        <div className="space-y-2" data-testid="blockers">
          {dr.blockers.map((b, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-lg p-4"
                 data-testid={`blocker-${i}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-stone-900">{b.capability}</h3>
                <span className={`text-[12px] font-semibold px-1.5 py-0.5 rounded ${
                  b.status === "REFUSED BY DESIGN"
                    ? "bg-stone-200 text-stone-700"
                    : b.status === "PARTIAL"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-rose-100 text-rose-800"}`}>
                  {b.status}
                </span>
              </div>
              <p className="text-xs text-stone-700 mt-1.5 leading-relaxed">
                <span className="font-semibold">Evidence:</span> {b.evidence}
              </p>
              <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                <span className="font-semibold">Unlocks with:</span> {b.unlocks_with}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Geography match levels"
               sub="Free-text district strings resolved against the LGD master. The worst level on a grant is the level reported for that grant.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="geo-levels">
          {Object.entries(dr.geo_match_levels).map(([k, v]) => (
            <Tile key={k} label={k} value={num(v)} />
          ))}
        </div>
      </Section>

      <Section title="Coverage by financial year">
        <DataTable
          testId="fy-coverage"
          rows={Object.entries(dr.fy_coverage).map(([fy, v]) => ({ fy, ...v }))}
          columns={[
            { key: "fy", label: "FY" },
            { key: "grants", label: "Grants", align: "right" },
            { key: "with_received", label: "Funds received", align: "right",
              render: (r) => `${r.with_received} (${pct((100 * r.with_received) / r.grants, 0)})` },
            { key: "with_utilised", label: "Funds utilised", align: "right",
              render: (r) => `${r.with_utilised} (${pct((100 * r.with_utilised) / r.grants, 0)})` },
            { key: "with_beneficiaries", label: "Beneficiary count", align: "right",
              render: (r) => `${r.with_beneficiaries} (${pct((100 * r.with_beneficiaries) / r.grants, 0)})` },
            { key: "with_district", label: "District mapped", align: "right",
              render: (r) => `${r.with_district} (${pct((100 * r.with_district) / r.grants, 0)})` },
            { key: "with_receipt_dates", label: "Actual receipt dates", align: "right",
              render: (r) => (
                <span className={r.with_receipt_dates ? "" : "text-rose-700"}>
                  {r.with_receipt_dates}
                </span>
              )},
          ]}
        />
      </Section>

      <Section title="Doctype inventory" sub="Every mGrant doctype profiled, with its record count.">
        <DataTable
          testId="doctype-inventory"
          maxHeight="max-h-96"
          rows={dr.doctype_inventory.slice().sort((a, b) => a.records - b.records)}
          columns={[
            { key: "doctype", label: "Doctype" },
            { key: "records", label: "Records", align: "right", render: (r) => num(r.records) },
            { key: "verdict", label: "Verdict",
              render: (r) => (
                <span className={r.records ? "text-stone-700" : "text-rose-700 font-medium"}>
                  {r.verdict}
                </span>
              )},
          ]}
        />
      </Section>

      <Section title="Field-level coverage" sub="Which fields are actually populated, and which are free text.">
        <div className="flex gap-1.5 mb-3" data-testid="coverage-tabs">
          {Object.keys(dr.field_coverage || {}).map((k) => (
            <button key={k} onClick={() => setCoverageOf(k)}
                    data-testid={`coverage-tab-${k}`}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      coverageOf === k ? "bg-[#760D28] text-white"
                        : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300"}`}>
              {k}
            </button>
          ))}
        </div>
        <DataTable
          testId="field-coverage"
          maxHeight="max-h-[30rem]"
          rows={coverage}
          columns={[
            { key: "field", label: "Field", className: "num text-[12px]" },
            { key: "coverage_pct", label: "Coverage", align: "right", render: (r) => (
              <span className={r.coverage_pct === 0 ? "text-rose-700 font-medium"
                : r.coverage_pct < 50 ? "text-amber-700" : ""}>
                {pct(r.coverage_pct)}
              </span>
            )},
            { key: "filled", label: "Filled / total", align: "right",
              render: (r) => `${num(r.filled)} / ${num(r.total)}` },
            { key: "type", label: "Type", align: "right" },
            { key: "distinct_sample", label: "Sample values",
              render: (r) => (
                <span className="num text-[12px] text-stone-600">
                  {r.distinct_sample.join(" · ") || "—"}
                </span>
              )},
          ]}
        />
      </Section>

      {reg && (
        <Section title="Metric registry"
                 sub={`${reg.live.length} live metrics, ${reg.refused.length} registered as refused. Version ${reg.registry_version}.`}>
          <DataTable
            testId="registry-table"
            maxHeight="max-h-[34rem]"
            rows={[...reg.live, ...reg.refused]}
            columns={[
              { key: "id", label: "Metric", render: (r) => (
                <div>
                  <div className="font-medium text-stone-900">{r.label}</div>
                  <div className="num text-[12px] text-stone-600">{r.id} · v{r.version} · {r.question}</div>
                  {r.definition && <div className="text-[12px] text-stone-600 mt-1 max-w-xl leading-relaxed">{r.definition}</div>}
                  {r.evidence && <div className="text-[12px] text-rose-700 mt-1 max-w-xl leading-relaxed">{r.evidence}</div>}
                </div>
              )},
              { key: "status", label: "Status", align: "right", render: (r) => (
                <span className={`text-[12px] font-semibold px-1.5 py-0.5 rounded ${
                  r.status === "live" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                  {r.status}
                </span>
              )},
              { key: "unit", label: "Unit", align: "right" },
            ]}
          />
        </Section>
      )}
    </div>
  );
}

const Section = ({ title, sub, children }) => (
  <section className="mb-8">
    <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
    {sub && <p className="text-sm text-stone-600 mt-1 mb-3 max-w-3xl leading-relaxed">{sub}</p>}
    {!sub && <div className="mb-3" />}
    {children}
  </section>
);

const Tile = ({ label, value }) => (
  <div className="bg-white border border-stone-200 rounded-xl px-4 py-3">
    <div className="text-[12px] text-stone-600">{label}</div>
    <div className="num text-xl font-medium text-stone-900 mt-0.5">{value}</div>
  </div>
);
